import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { X, Plus, Minus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export const StockingBuilder = ({ open, onClose, stocking, stuffers, picks = 6 }) => {
  const { addItem } = useCart();
  const [counts, setCounts] = useState({});
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const change = (name, d) => {
    if (d > 0 && total >= picks) {
      toast.error(`Your stocking holds ${picks} stuffers`);
      return;
    }
    setCounts((c) => ({ ...c, [name]: Math.max(0, (c[name] || 0) + d) }));
  };

  const add = () => {
    if (total !== picks) {
      toast.error(`Pick ${picks - total} more stuffer${picks - total === 1 ? "" : "s"}`);
      return;
    }
    const list = Object.entries(counts).flatMap(([n, q]) => Array(q).fill(n));
    addItem(stocking, null, 1, null, `Stuffers: ${list.join(", ")}`);
    confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, colors: ["#D7263D", "#1F9D55", "#FFD700", "#FFFFFF"] });
    toast.success("Your stocking is in the cart! 🧦");
    setCounts({});
    onClose();
  };

  return (
    <AnimatePresence>
      {open && stocking && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4"
          onClick={onClose} data-testid="stocking-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
            className="bg-candy-bg border-4 border-candy-gold rounded-2xl shadow-brutal-lg w-full max-w-lg max-h-[90vh] overflow-y-auto text-candy-cream"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b-4 border-black bg-candy-pink text-white sticky top-0 z-10">
              <h3 className="font-spooky text-3xl">Build Your Stocking</h3>
              <button data-testid="stocking-close" onClick={onClose} className="border-2 border-black bg-candy-card rounded-lg p-1.5">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <p className="font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="stocking-count">
                Pick {picks} stuffers ({total}/{picks})
              </p>
              <ul className="mt-3 space-y-2">
                {stuffers.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 bg-candy-card border-2 border-black rounded-xl p-2">
                    <img src={s.image} alt={s.name} className="h-12 w-12 rounded-lg object-cover" />
                    <span className="flex-1 font-heading font-bold text-sm">{s.name}</span>
                    <button data-testid={`stocking-dec-${s.id}`} onClick={() => change(s.name, -1)} className="p-1.5 border-2 border-candy-teal rounded-lg" aria-label="remove one">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span data-testid={`stocking-qty-${s.id}`} className="w-5 text-center font-bold">{counts[s.name] || 0}</span>
                    <button data-testid={`stocking-inc-${s.id}`} onClick={() => change(s.name, 1)} className="p-1.5 border-2 border-candy-teal rounded-lg" aria-label="add one">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-5 border-t-4 border-black bg-candy-card sticky bottom-0 flex items-center gap-3">
              <span className="font-display text-3xl text-candy-gold">${stocking.price.toFixed(2)}</span>
              <button
                data-testid="stocking-add-to-cart"
                onClick={add}
                className="flex-1 bg-candy-pink text-white font-bold uppercase tracking-wider py-3 border-4 border-black rounded-xl shadow-brutal-sm"
              >
                Add Stocking to Cart
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
